import React from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    LinearProgress,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import EventIcon from '@material-ui/icons/Event'
import RoomIcon from '@material-ui/icons/Room'
import PersonIcon from '@material-ui/icons/Person'
import ScheduleIcon from '@material-ui/icons/Schedule'
import { Container } from '../../components'
import {
    PRIORITY_COLORS,
    PRIORITY_LABELS,
    VENDOR_PROJECT_BUCKET_COLORS,
    VENDOR_TASK_STATUS_COLORS,
    VENDOR_TASK_STATUS_LABELS,
    VendorProject,
    getProjectBucket,
    safeDateOnly,
} from '../../models'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    open: boolean
    project?: VendorProject
    onClose: () => void
}

interface RowProps {
    icon?: React.ReactNode
    label: string
    value: React.ReactNode
}

const Row = ({ icon, label, value }: RowProps) => {
    const theme = useTheme()
    return (
        <Container direction="column">
            <Container
                alignItems="center"
                style={{
                    ...typographyStyle(theme.typography.caption),
                    color: theme.palette.text.secondary,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    gap: 4,
                }}
            >
                {icon}
                {label}
            </Container>
            <Container
                style={{
                    ...typographyStyle(theme.typography.body2),
                    marginTop: theme.spacing(0.5),
                    whiteSpace: 'pre-wrap',
                    color: theme.palette.text.primary,
                }}
            >
                {value || (
                    <span style={{ color: theme.palette.text.disabled }}>
                        —
                    </span>
                )}
            </Container>
        </Container>
    )
}

export const ProjectViewModal = (props: Props) => {
    const { open, project, onClose } = props
    const theme = useTheme()

    if (!project) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogContent />
            </Dialog>
        )
    }

    const tasks = project.tasks ?? []
    const taskCount = project.taskCount ?? tasks.length
    const completed = project.completedTaskCount ?? 0
    const inProgress = project.inProgressTaskCount ?? 0
    const completionPct =
        taskCount > 0 ? Math.round((completed / taskCount) * 100) : 0

    const bucket = getProjectBucket(project)
    const bucketColors = VENDOR_PROJECT_BUCKET_COLORS[bucket]
    const priorityColors = PRIORITY_COLORS[project.priority]

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle disableTypography style={{ padding: 0 }}>
                <Container
                    direction="column"
                    style={{
                        background: `linear-gradient(135deg, ${theme.palette.brand1.main} 0%, #1f4848 100%)`,
                        color: 'white',
                        padding: theme.spacing(2.5, 3),
                    }}
                >
                    <Container
                        alignItems="center"
                        style={{ gap: theme.spacing(1) }}
                    >
                        <Container direction="column" flex={1}>
                            <Container
                                style={{
                                    ...typographyStyle(theme.typography.h6),
                                    fontWeight: 700,
                                    color: 'white',
                                }}
                            >
                                {project.title}
                            </Container>
                            <Container
                                alignItems="center"
                                style={{
                                    gap: theme.spacing(0.75),
                                    marginTop: theme.spacing(1),
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Container
                                    alignItems="center"
                                    style={{
                                        backgroundColor: bucketColors.bg,
                                        color: bucketColors.text,
                                        padding: theme.spacing(0.25, 1),
                                        borderRadius: 999,
                                        fontWeight: 700,
                                        ...typographyStyle(
                                            theme.typography.caption,
                                        ),
                                    }}
                                >
                                    {bucketColors.label}
                                </Container>
                                <Container
                                    alignItems="center"
                                    style={{
                                        backgroundColor: priorityColors.bg,
                                        color: priorityColors.text,
                                        padding: theme.spacing(0.25, 1),
                                        borderRadius: 999,
                                        fontWeight: 700,
                                        ...typographyStyle(
                                            theme.typography.caption,
                                        ),
                                    }}
                                >
                                    {PRIORITY_LABELS[project.priority]} priority
                                </Container>
                            </Container>
                        </Container>
                        <IconButton
                            size="small"
                            onClick={onClose}
                            style={{ color: 'white' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Container>
                </Container>
            </DialogTitle>
            <DialogContent style={{ paddingTop: theme.spacing(3) }}>
                <Container
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: theme.spacing(2),
                        rowGap: theme.spacing(2),
                    }}
                >
                    <Row
                        icon={<EventIcon style={{ fontSize: 12 }} />}
                        label="Start Date"
                        value={safeDateOnly(project.start_date) ?? ''}
                    />
                    <Row
                        icon={<EventIcon style={{ fontSize: 12 }} />}
                        label="End Date"
                        value={safeDateOnly(project.end_date) ?? ''}
                    />
                    <Row
                        icon={<RoomIcon style={{ fontSize: 12 }} />}
                        label="Address"
                        value={project.address}
                    />
                    <Row
                        icon={<PersonIcon style={{ fontSize: 12 }} />}
                        label="Assigned By"
                        value={project.assigned_by?.name_field ?? ''}
                    />
                    <Row
                        icon={<ScheduleIcon style={{ fontSize: 12 }} />}
                        label="Created"
                        value={safeDateOnly(project.created_date) ?? ''}
                    />
                    <Row
                        icon={<ScheduleIcon style={{ fontSize: 12 }} />}
                        label="Last Modified"
                        value={safeDateOnly(project.modified_date) ?? ''}
                    />
                </Container>

                {project.description && (
                    <Container
                        direction="column"
                        style={{ marginTop: theme.spacing(3) }}
                    >
                        <Container
                            style={{
                                ...typographyStyle(theme.typography.caption),
                                color: theme.palette.text.secondary,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            Description
                        </Container>
                        <Container
                            style={{
                                padding: theme.spacing(1.5),
                                backgroundColor: '#f7f8f9',
                                borderRadius: 8,
                                ...typographyStyle(theme.typography.body2),
                                whiteSpace: 'pre-wrap',
                            }}
                        >
                            {project.description}
                        </Container>
                    </Container>
                )}

                <Divider style={{ margin: theme.spacing(3, 0, 2) }} />

                <Container alignItems="center">
                    <Container
                        style={{
                            ...typographyStyle(theme.typography.subtitle2),
                            fontWeight: 700,
                        }}
                    >
                        Tasks
                        <span
                            style={{
                                marginLeft: 6,
                                color: theme.palette.text.secondary,
                                fontWeight: 400,
                            }}
                        >
                            ({taskCount})
                        </span>
                    </Container>
                    <Container flex={1} />
                    <Container
                        style={{
                            ...typographyStyle(theme.typography.caption),
                            color: theme.palette.text.secondary,
                        }}
                    >
                        {completed} done · {inProgress} in progress
                    </Container>
                </Container>

                {taskCount > 0 && (
                    <Container
                        direction="column"
                        style={{ marginTop: theme.spacing(1) }}
                    >
                        <LinearProgress
                            variant="determinate"
                            value={completionPct}
                            style={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: '#eceff1',
                            }}
                        />
                    </Container>
                )}

                <Container
                    direction="column"
                    style={{ marginTop: theme.spacing(1.5) }}
                >
                    {tasks.length === 0 ? (
                        <Container
                            style={{
                                padding: theme.spacing(2),
                                color: theme.palette.text.disabled,
                                fontStyle: 'italic',
                                textAlign: 'center',
                                border: `1px dashed ${theme.palette.divider}`,
                                borderRadius: 8,
                            }}
                        >
                            No tasks for this project.
                        </Container>
                    ) : (
                        tasks.map((t) => {
                            const colors = VENDOR_TASK_STATUS_COLORS[t.status]
                            return (
                                <Container
                                    key={t.id}
                                    alignItems="center"
                                    style={{
                                        padding: theme.spacing(1, 1.5),
                                        borderLeft: `4px solid ${colors.fg}`,
                                        backgroundColor: 'white',
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderLeftWidth: 4,
                                        borderLeftColor: colors.fg,
                                        borderRadius: 6,
                                        marginBottom: theme.spacing(0.75),
                                    }}
                                >
                                    <Container
                                        flex={1}
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.body2,
                                            ),
                                            fontWeight: 600,
                                        }}
                                    >
                                        {t.title}
                                    </Container>
                                    <Container
                                        style={{
                                            backgroundColor: colors.bg,
                                            color: colors.text,
                                            padding: theme.spacing(0.25, 1),
                                            borderRadius: 999,
                                            ...typographyStyle(
                                                theme.typography.caption,
                                            ),
                                            fontWeight: 700,
                                        }}
                                    >
                                        {VENDOR_TASK_STATUS_LABELS[t.status]}
                                    </Container>
                                </Container>
                            )
                        })
                    )}
                </Container>
            </DialogContent>
            <DialogActions style={{ padding: theme.spacing(2, 3) }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onClose}
                    disableElevation
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}
